import {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  Select,
  MenuItem,
  Box,
  Divider,
  useTheme,
  ButtonGroup,
  Button,
  FormControlLabel,
  Switch,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import Editor, {
  fontSizes,
  fontFamilies,
  DEFAULT_FONT_SIZE,
} from './rich-text-editor/Editor';
import { tokens } from '../../global/theme/tokens';
import ExoColorPicker from '../special/ExoColorPicker';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import { useTranslation } from 'react-i18next';
import Cookies from 'universal-cookie';
import { TextBlockStyle } from '../editor/styling-controls/TextBlockStyle';
import { SignatureBlockStyle } from '../editor/styling-controls/SignatureBlockStyle';
import { BookingLinkStyle } from '../editor/styling-controls/BookingLinkStyle';
import { AiRewriteStyle } from '../editor/styling-controls/AiRewriteStyle';
import { Delta } from 'quill';
import { convertQuillHtmlToInlineAndClassHtml } from './rich-text-editor/convertQuillHtmlToInlineAndClassHtml.js';
import { useUserPreferences } from '../../global/useUserPreferences.jsx';
import { htmlToQuillDelta } from './rich-text-editor/quillHtmlToDelta.ts';

export const ExoRichTextEditor = forwardRef(
  (
    {
      debug,
      startHtml,
      onHtmlChange,
      hasError,
      textBlockType,
      allowSignature,
      // Off lets a caller provide the signature for the toolbar button without
      // it being written into an empty editor on mount.
      autoInsertSignature = true,
      allowBookingLink,
      // The "improve with AI" button (hidden without the AI chat permission).
      allowAiRewrite,
      signature,
      afterComponent,
      defaultColor,
      defaultFontSize,
      bgcolor,
      emailCredentials,
    },
    ref
  ) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
    const cookies = new Cookies();

    const { t, i18n } = useTranslation();

    const quillRef = useRef(null);
    const lastRangeRef = useRef(null);

    const [debugHtml, setDebugHtml] = useState('');

    const [readOnly, setReadOnly] = useState(false);
    const [range, setRange] = useState(null);
    const [lastChange, setLastChange] = useState(null);
    // highlight buttons
    const [activeFormats, setActiveFormats] = useState({});

    useEffect(() => {
      if (autoInsertSignature && !startHtml && signature?.text) {
        handleInsertDelta(htmlToQuillDelta(signature.text), true, true);
      }
    }, [startHtml, signature, autoInsertSignature]);


    const handleSelectionChange = (range, oldRange, source) => {
      setRange(range);
      lastRangeRef.current = range;

      if (quillRef.current && range) {
        const formats = quillRef.current.getFormat(range);
        setActiveFormats(formats);
      }
    };

    function handleHtmlChange(e) {
      onHtmlChange(e);
      setDebugHtml(e);
    }

    // Funktion um Formatierung selektiv und fokussiert zu setzen
    const handlePreserveSelection = (callback) => (e) => {
      e.preventDefault(); // verhindert Verlust des Fokus
      const quill = quillRef.current;
      const range = lastRangeRef.current;

      setTimeout(() => {
        if (quill && range) {
          quill.setSelection(range);
        }
        callback(e); // Format anwenden
      }, 0);
    };

    // handle button press
    const formatText = (format) => {
      const quill = quillRef.current;
      setActiveFormats({ ...activeFormats, [format]: !activeFormats[format] });
      if (quill) {
        const range = quill.getSelection();
        if (range) {
          const formats = quill.getFormat(range);
          quill.format(format, !formats[format]); // Toggle
        }
      }
    };

    // FONT-FAMILY Handler
    const handleFontChange = (e) => {
      const value = e.target.value || false;
      cookies.set('rtf-font', value);
      quillRef.current?.format('font', value);
      setActiveFormats({
        ...activeFormats,
        font: value,
      });
    };

    // FONT-SIZE Handler
    const handleSizeChange = (e) => {
      const value = e.target.value || false;
      quillRef.current?.format('size', value);
      setActiveFormats({
        ...activeFormats,
        size: value,
      });
    };

    // FONT-COLOR Handler
    const [openColor, setOpenColor] = useState(false);
    const handleColorChange = (value) => {
      quillRef.current?.format('color', value);
      setActiveFormats({
        ...activeFormats,
        color: value,
      });
    };

    const handleList = (type) => {
      quillRef.current?.format('list', type === activeFormats.list ? '' : type);
      setActiveFormats({
        ...activeFormats,
        list: type === activeFormats.list ? '' : type,
      });
    };

    // generate classes for fontsize
    function generateSxForFontSize() {
      var sx = {};
      fontSizes.forEach((size) => {
        sx[`.ql-size-${size}`] = { fontSize: size };
      });

      return sx;
    }

    // insert text
    const lastSelectionRef = useRef(null);

    // Track the last known selection
    useEffect(() => {
      const editor = quillRef.current;
      if (!editor) return;

      editor.on('selection-change', (range) => {
        if (range) {
          lastSelectionRef.current = range;
        }
      });
    }, []);

    function getSafeRange() {
      const editor = quillRef.current;
      if (!editor) return null;

      // Try current selection first
      let range = editor.getSelection();

      // Fallback to last known selection
      if (!range && lastSelectionRef.current) {
        range = lastSelectionRef.current;
      }

      // If still none, set to end of document
      if (!range) {
        const length = editor.getLength();
        range = { index: length, length: 0 };
      }

      return range;
    }

    function handleInsertDelta(
      delta,
      moveToStart = false,
      createNewLine = false
    ) {
      const editor = quillRef.current;
      if (!editor) return;

      const range = getSafeRange();
      if (!range) return;

      editor.updateContents(
        new Delta().retain(range.index).concat(delta),
        'user'
      );

      // Calculate inserted length
      const insertedLength = delta.reduce((len, op) => {
        if (typeof op.insert === 'string') {
          return len + op.insert.length;
        } else {
          return len + 1; // for embeds
        }
      }, 0);

      if (createNewLine) {
        // Insert newline at the very beginning
        editor.insertText(0, '\n', 'user');
      }

      editor.setSelection(
        moveToStart ? 0 : range.index + insertedLength,
        0,
        'silent'
      );
    }

    function handleInsertHtml(html, position = 'cursor') {
      const editor = quillRef.current;
      if (!editor) return;

      let insertIndex;
      if (position === 'start') {
        insertIndex = 0;
      } else if (position === 'end') {
        insertIndex = editor.getLength() - 1;
      } else if (typeof position === 'number') {
        insertIndex = position;
      } else {
        // 'cursor' - default behavior
        const range = getSafeRange();
        if (!range) return;
        insertIndex = range.index;
      }

      const delta = htmlToQuillDelta(html);
      editor.updateContents(
        new Delta().retain(insertIndex).concat(delta),
        'user'
      );
    }

    // Insert plain text with optional format at specified position
    function handleInsertText(text, format = {}, position = 'cursor') {
      const editor = quillRef.current;
      if (!editor) return;

      let insertIndex;
      if (position === 'start') {
        insertIndex = 0;
      } else if (position === 'end') {
        insertIndex = editor.getLength() - 1;
      } else if (typeof position === 'number') {
        insertIndex = position;
      } else {
        // 'cursor' - default behavior
        const range = getSafeRange();
        if (!range) return;
        insertIndex = range.index;
      }

      editor.insertText(insertIndex, text, format, 'user');
    }

    // Replace selected text with new text and format
    function handleReplaceSelection(text, format = {}) {
      const editor = quillRef.current;
      if (!editor) return;

      const range = editor.getSelection();
      if (!range) return;

      // Delete selected text first if there's a selection
      if (range.length > 0) {
        editor.deleteText(range.index, range.length, 'user');
      }

      // Insert new text at the position
      editor.insertText(range.index, text, format, 'user');
    }

    // Replace entire content with HTML
    function handleSetContent(html) {
      const editor = quillRef.current;
      if (!editor) return;

      const delta = htmlToQuillDelta(html);
      editor.setContents(delta, 'user');
    }

    // Get all content formats
    function handleGetContent() {
      const editor = quillRef.current;
      if (!editor) {
        return { html: '', inlineHtml: '', plainText: '', delta: new Delta() };
      }

      const quillHtml = editor.root.innerHTML;
      const plainText = editor.getText().trim();
      const inlineHtml = convertQuillHtmlToInlineAndClassHtml(
        quillHtml,
        defaultFontSize || DEFAULT_FONT_SIZE
      );
      const delta = editor.getContents();

      return {
        html: quillHtml,
        inlineHtml,
        plainText,
        delta,
        characterCount: plainText.length,
      };
    }

    // Get fully inline-styled HTML
    function handleGetInlineHtml() {
      const editor = quillRef.current;
      if (!editor) return '';

      const quillHtml = editor.root.innerHTML;
      return convertQuillHtmlToInlineAndClassHtml(
        quillHtml,
        defaultFontSize || DEFAULT_FONT_SIZE
      );
    }

    // Focus the editor
    function handleFocus() {
      const editor = quillRef.current;
      if (!editor) return;
      editor.focus();
    }

    // Blur the editor
    function handleBlur() {
      const editor = quillRef.current;
      if (!editor) return;
      editor.blur();
    }

    // Get current selection
    function handleGetSelection() {
      const editor = quillRef.current;
      if (!editor) return null;
      return editor.getSelection();
    }

    // Set selection
    function handleSetSelection(index, length = 0) {
      const editor = quillRef.current;
      if (!editor) return;
      editor.setSelection(index, length, 'user');
    }

    // Get format at range
    function handleGetFormat(range) {
      const editor = quillRef.current;
      if (!editor) return {};

      if (range) {
        return editor.getFormat(range);
      }
      // If no range provided, get format at current selection
      const selection = editor.getSelection();
      return selection ? editor.getFormat(selection) : {};
    }

    // Apply format
    function handleFormat(name, value) {
      const editor = quillRef.current;
      if (!editor) return;
      editor.format(name, value, 'user');
    }

    // Clear all content
    function handleClear() {
      const editor = quillRef.current;
      if (!editor) return;
      editor.setText('', 'user');
    }

    useImperativeHandle(ref, () => ({
      // Existing methods
      insertHtml: handleInsertHtml,
      insertDelta: handleInsertDelta,

      // New text insertion methods
      insertText: handleInsertText,
      replaceSelection: handleReplaceSelection,

      // Content management methods
      setContent: handleSetContent,
      getContent: handleGetContent,
      getInlineHtml: handleGetInlineHtml,
      clear: handleClear,

      // Editor control methods
      focus: handleFocus,
      blur: handleBlur,
      getRoot: () => quillRef.current?.root,
      getSelection: handleGetSelection,
      setSelection: handleSetSelection,

      // Format methods
      getFormat: handleGetFormat,
      format: handleFormat,
    }));

    return (
      <Box className="flex flex-col gap-2 w-full" sx={generateSxForFontSize()}>
        <Box
          className="rounded-lg flex gap-2 flex-wrap"
          sx={{
            'button.grouped': {
              bgcolor: colors.bgInput,
              border: `1px solid transparent`,
              minHeight: '37px',
              ':hover': {
                bgcolor: colors.card,
              },
            },
            '.active-style': {
              bgcolor: colors.info + '30!important',
              border: `1px solid ${colors.info}80!important`,
            },
          }}
        >
          <ButtonGroup variant="contained" size="small" disableElevation>
            <Button
              className={
                'grouped ' + (activeFormats.bold ? 'active-style' : '')
              }
              onClick={() => formatText('bold')}
            >
              <FormatBoldIcon />
            </Button>
            <Button
              className={
                'grouped ' + (activeFormats.italic ? 'active-style' : '')
              }
              onClick={() => formatText('italic')}
            >
              <FormatItalicIcon />
            </Button>
            <Button
              className={
                'grouped ' + (activeFormats.underline ? 'active-style' : '')
              }
              onClick={() => formatText('underline')}
            >
              <FormatUnderlinedIcon />
            </Button>
          </ButtonGroup>
          <Button
            onClick={() => {
              if (activeFormats.color && activeFormats.color !== defaultColor) {
                // reset color
                handleColorChange(defaultColor || null);
              } else {
                setOpenColor(true);
              }
            }}
            sx={{
              width: '37px',
              height: '37px',
              minWidth: 'unset',
              backgroundColor: activeFormats.color || colors.bgInput,
              ':hover': {
                bgcolor: activeFormats.color || colors.card,
              },
            }}
          >
            <FormatColorTextIcon />
          </Button>
          <ExoColorPicker
            open={openColor}
            onClose={() => setOpenColor(false)}
            color={activeFormats.color}
            onSelect={handleColorChange}
          />
          <Divider orientation="vertical" flexItem />
          <ButtonGroup variant="contained" size="small" disableElevation>
            <Button
              className={
                'grouped ' +
                (activeFormats.list === 'bullet' ? 'active-style' : '')
              }
              onClick={() => handleList('bullet')}
            >
              <FormatListBulletedIcon />
            </Button>

            <Button
              className={
                'grouped ' +
                (activeFormats.list === 'ordered' ? 'active-style' : '')
              }
              onClick={() => handleList('ordered')}
            >
              <FormatListNumberedIcon />
            </Button>
          </ButtonGroup>
          <Divider orientation="vertical" flexItem />
          {false && (
            <Select
              size="small"
              value={activeFormats.font || ''}
              displayEmpty
              onMouseDown={handlePreserveSelection(() => {})}
              onChange={handlePreserveSelection(handleFontChange)}
            >
              {fontFamilies.map((f) => (
                <MenuItem key={f} value={f}>
                  {f}
                </MenuItem>
              ))}
            </Select>
          )}
          <Select
            size="small"
            value={
              activeFormats.size || defaultFontSize || DEFAULT_FONT_SIZE
            }
            displayEmpty
            onMouseDown={handlePreserveSelection(() => {})}
            onChange={handlePreserveSelection(handleSizeChange)}
          >
            {fontSizes.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
          <Divider orientation="vertical" flexItem />
          {textBlockType && (
            <TextBlockStyle
              onInsert={handleInsertDelta}
              onChange={onHtmlChange}
              textBlockType={textBlockType}
            />
          )}
          {allowSignature && (
            <SignatureBlockStyle
              onInsert={handleInsertDelta}
              selectedSignature={signature}
              credentials={emailCredentials}
            />
          )}
          {allowBookingLink && (
            <BookingLinkStyle onInsert={handleInsertDelta} />
          )}
          {allowAiRewrite && (
            <AiRewriteStyle
              getEditor={() => quillRef.current}
              getRange={getSafeRange}
              hasSelection={Boolean(range && range.length > 0)}
            />
          )}
        </Box>
        <Editor
          ref={quillRef}
          readOnly={readOnly}
          defaultValue={startHtml}
          onSelectionChange={handleSelectionChange}
          onTextChange={setLastChange}
          onHtmlChange={handleHtmlChange}
          hasError={hasError}
          afterComponent={afterComponent}
          defaultColor={defaultColor}
          defaultFontSize={defaultFontSize}
          bgcolor={bgcolor}
          lang={i18n.language}
        />
        {debug && (
          <Box>
            <Box mt={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={readOnly}
                    onChange={(e) => setReadOnly(e.target.checked)}
                  />
                }
                label="Read Only"
              />

              <Button
                sx={{ ml: 2 }}
                variant="outlined"
                onClick={() => {
                  alert(`Length: ${quillRef.current?.getLength()}`);
                }}
              >
                Get Content Length
              </Button>
            </Box>

            <Box mt={2}>
              <strong>Current Range:</strong>
              <br />
              {range ? JSON.stringify(range) : 'None'}
            </Box>

            <Box mt={1}>
              <strong>Current Styles:</strong>
              <br />
              {activeFormats ? JSON.stringify(activeFormats) : 'None'}
            </Box>

            <Box mt={1}>
              <strong>Last Change:</strong>
              <br />
              {lastChange ? JSON.stringify(lastChange.ops) : 'None'}
            </Box>
            <Box>
              <strong>HTML:</strong>
              {debugHtml}
              <br />
            </Box>
          </Box>
        )}
      </Box>
    );
  }
);
